import {
  Box,
  InputVariant,
  PasswordInput,
  Popover,
  Progress,
  Text,
} from "@mantine/core";
import React, { forwardRef, ReactNode, useEffect, useState } from "react";
import { IconCheck, IconX } from "@tabler/icons";
import { useColors } from "../../../hooks/colors/useColors";
import { UseFormRegister, UseFormWatch } from "react-hook-form/dist/types/form";
import { useTranslation } from "next-i18next";

type InputProps = {
  placeholder?: string;
  variant?: InputVariant;
  errors: ReactNode;
  value?: string;
  icon?: ReactNode;
  styles?: Record<string, any>;
  //react hook form register
  register: UseFormRegister<any>;
  //react hook form watch
  watch: UseFormWatch<any>;
  field: string;
  onTextChange?: any;
};

// eslint-disable-next-line react/display-name
export const PasswordValidationInput = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const { colors } = useColors();
    const { placeholder, variant, icon, register, watch, field, onTextChange } =
      props;
    const [popoverOpened, setPopoverOpened] = useState(false);
    const [value, setValue] = useState("");
    const { t } = useTranslation("common");

    //set local value to check password strength
    useEffect(() => {
      const subscription = watch((value) => {
        const typedValue: string = value[field];
        setValue(typedValue);
      });
      return () => subscription.unsubscribe();
    }, [watch, field]);

    const requirements = [
      { re: /[0-9]/, label: t("reg-fig1") },
      { re: /[a-z]/, label: t("reg-reg1") },
      { re: /[A-Z]/, label: t("reg-cap1") },
    ];

    function getStrength(password: string) {
      let multiplier = password.length > 7 ? 0 : 1;

      requirements.forEach((requirement) => {
        if (!requirement.re.test(password)) {
          multiplier += 1;
        }
      });

      // console.log(Math.max(100 - (100 / (requirements.length+1)) * multiplier))
      return Math.max(
        Math.max(100 - (100 / (requirements.length + 1)) * multiplier)
      );
    }

    const styles = {
      border: "1px solid var(--gray-slot-color)",
      backgroundColor: variant ? undefined : colors.background,
      height: 30,
    };

    function PasswordRequirement({
      meets,
      label,
    }: {
      meets: boolean;
      label: string;
    }) {
      return (
        <Text
          className="flex items-center"
          color={meets ? colors.success : colors.danger}
          size="sm"
        >
          {meets ? <IconCheck size={14} /> : <IconX size={14} />}{" "}
          <Box ml={10}>{label}</Box>
        </Text>
      );
    }

    const strength = getStrength(value);
    const color =
      strength === 100
        ? colors.success
        : strength > 50
        ? colors.yellow
        : colors.danger;
    const checks = requirements.map((requirement, index) => (
      <PasswordRequirement
        key={index}
        label={requirement.label}
        meets={requirement.re.test(value)}
      />
    ));

    return (
      <div style={{ margin: "auto" }}>
        <Popover
          opened={popoverOpened}
          position="bottom"
          width="target"
          // transition="pop"
        >
          <Popover.Target>
            <div
              onFocusCapture={() => setPopoverOpened(true)}
              onBlurCapture={() => setPopoverOpened(false)}
            >
              <PasswordInput
                label={placeholder}
                leftSection={icon}
                withAsterisk
                variant={variant}
                placeholder={placeholder}
                {...register(field, {
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,60}$/,
                    message: t("login.invalidPassword"),
                  },
                })}
              />
            </div>
          </Popover.Target>
          <Popover.Dropdown>
            <Progress
              color={color}
              value={strength}
              size={5}
              style={{ marginBottom: 10 }}
            />
            <PasswordRequirement
              label={t("reg-char8")}
              meets={value.length > 7}
            />
            {checks}
          </Popover.Dropdown>
        </Popover>
      </div>
    );
  }
);
